
import React, { useState, useCallback } from 'react';
import { Header } from './components/Header';
import { InitialForm } from './components/InitialForm';
import ChatDisplay from './components/ChatDisplay';
import { DiagnosisReport } from './components/DiagnosisReport';
import { FixStepsDisplay } from './components/FixStepsDisplay';
import { LoadingSpinner } from './components/LoadingSpinner';
import { startChatSession, continueChatSession, getFixSteps } from './services/aiServiceFactory';
import type { VehicleInfo, ChatMessage, AIDiagnosis, AIQuestion, AIDiagnosisCause, AIFixSteps, ChatSession } from './types';

type AppStep = 'initial' | 'chatting' | 'diagnosis' | 'fixing' | 'error';

const AppContent: React.FC = () => {
  const [step, setStep] = useState<AppStep>('initial');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [currentAIResponse, setCurrentAIResponse] = useState<AIQuestion | AIDiagnosis | null>(null);
  const [chatSession, setChatSession] = useState<ChatSession | null>(null);
  const [vehicleInfo, setVehicleInfo] = useState<VehicleInfo | null>(null);
  const [selectedCause, setSelectedCause] = useState<AIDiagnosisCause | null>(null);
  const [fixSteps, setFixSteps] = useState<AIFixSteps | null>(null);
  const [resetFormTrigger, setResetFormTrigger] = useState<number>(0);

  const handleReset = useCallback(() => {
    setStep('initial');
    setIsLoading(false);
    setError(null);
    setChatHistory([]);
    setCurrentAIResponse(null);
    setChatSession(null);
    setVehicleInfo(null);
    setSelectedCause(null);
    setFixSteps(null);
    // Trigger form reset by updating the trigger value
    setResetFormTrigger(prev => prev + 1);
  }, []);

  const handleStartDiagnosis = useCallback(async (info: VehicleInfo) => {
    setIsLoading(true);
    setError(null);
    setChatHistory([]);
    setVehicleInfo(info);
    
    setChatHistory([{ role: 'user', text: `Starting new diagnosis for a ${info.year} ${info.make} ${info.model}.` }]);

    try {
      const { chat, response } = await startChatSession(info);
      setChatSession(chat);
      
      if (response.type === 'question') {
          setChatHistory(prev => [...prev, { role: 'model', text: response.questionText }]);
          setCurrentAIResponse(response);
          setStep('chatting');
      } else {
          setCurrentAIResponse(response);
          setStep('diagnosis');
      }
    } catch (e) {
      console.error(e);
      setError('Failed to start the diagnosis session. Please check your connection and API key, then try again.');
      setStep('error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleAnswerSubmit = useCallback(async (answer: string) => {
    if (!chatSession) {
        setError('Chat session is not initialized.');
        setStep('error');
        return;
    }

    setIsLoading(true);
    setError(null);
    setChatHistory(prev => [...prev, { role: 'user', text: answer }]);

    try {
        const response = await continueChatSession(chatSession, answer);
        
        if (response.type === 'question') {
            setChatHistory(prev => [...prev, { role: 'model', text: response.questionText }]);
            setCurrentAIResponse(response);
        } else if (response.type === 'diagnosis') {
            setChatHistory(prev => [...prev, { role: 'model', text: 'Here is the final diagnosis based on your answers.' }]);
            setCurrentAIResponse(response);
            setStep('diagnosis');
        }

    } catch (e) {
        console.error(e);
        setError('An error occurred while communicating with the AI. Please try again.');
        setStep('error');
    } finally {
        setIsLoading(false);
    }
  }, [chatSession]);
  
  const handleSelectCause = useCallback(async (cause: AIDiagnosisCause) => {
    if (!vehicleInfo) {
        setError('Vehicle information is missing.');
        setStep('error');
        return;
    }
    setIsLoading(true);
    setError(null);
    setSelectedCause(cause);
    setStep('fixing');

    try {
        const steps = await getFixSteps(vehicleInfo, cause);
        setFixSteps(steps);
    } catch (e) {
        console.error(e);
        setError('Failed to get repair steps. Please try again or start over.');
        setStep('error');
    } finally {
        setIsLoading(false);
    }
  }, [vehicleInfo]);


  const renderContent = () => {
    if (isLoading && step !== 'chatting' && step !== 'fixing') {
        return <div className="flex justify-center items-center p-8"><LoadingSpinner /></div>;
    }
    
    if (step === 'error') {
        return (
            <div className="text-center p-8 bg-red-50 border border-red-200 rounded-lg">
                <h3 className="text-lg font-semibold text-red-800">An Error Occurred</h3>
                <p className="text-red-600 mt-2">{error}</p>
                <button
                    type="button"
                    onClick={handleReset}
                    className="mt-4 px-4 py-2 bg-[#002A5C] text-white font-semibold rounded-md hover:bg-[#001F44] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                    Start Over
                </button>
            </div>
        );
    }

    switch (step) {
      case 'initial':
        return <InitialForm onStartDiagnosis={handleStartDiagnosis} onReset={resetFormTrigger} />;
      case 'chatting':
        return <ChatDisplay 
                    history={chatHistory} 
                    currentQuestion={currentAIResponse as AIQuestion} 
                    onAnswer={handleAnswerSubmit} 
                    isLoading={isLoading} 
                />;
      case 'diagnosis':
        return <DiagnosisReport 
                    report={currentAIResponse as AIDiagnosis} 
                    onReset={handleReset} 
                    onSelectCause={handleSelectCause}
                    vehicleInfo={vehicleInfo!}
                />;
      case 'fixing':
          if (isLoading || !fixSteps || !selectedCause) {
              return <div className="flex justify-center items-center p-8 min-h-[400px]"><LoadingSpinner /></div>;
          }
          return <FixStepsDisplay 
                    fix={fixSteps}
                    cause={selectedCause.cause}
                    onReset={handleReset}
                 />;
      default:
        return <InitialForm onStartDiagnosis={handleStartDiagnosis} onReset={resetFormTrigger} />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 font-sans text-gray-800">
      <Header
        onReset={handleReset}
      />
      <main className="container mx-auto max-w-2xl p-4 sm:p-6">
        <div className="bg-white rounded-xl shadow-lg transition-all duration-300">
           {renderContent()}
        </div>
        <footer className="text-center text-sm text-gray-500 mt-6 pb-4">
            <div className="border-t border-gray-200 pt-6">
                <p className="font-semibold mb-1 text-gray-600">
                    ⚠️ AI-Generated Guidance
                </p>
                <p>
                    AutoAI is an experimental tool and may produce inaccurate information. Always verify steps with official service manuals and use professional judgment.
                </p>
                <p className="mt-4 text-xs text-gray-400">©2025 VPS a V2X Company</p>
            </div>
        </footer>
      </main>
    </div>
  );
};

const FindAFixApp: React.FC = () => {
  return <AppContent />;
};

export default FindAFixApp;
